/** Client list (spec §8.6). */

import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Car, FilePlus2, MoreHorizontal, Plus } from 'lucide-react';
import { createClientSchema, type ClientDto, type CreateClientInput } from '@ias/shared';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DataTable, type DataColumn } from '@/components/data-table/data-table';
import { AutoForm, type FieldDescriptor } from '@/components/form/auto-form';
import { EntityAvatar } from '@/components/common/entity-avatar';
import { EnumBadge } from '@/components/common/enum-badge';
import { PageHeader } from '@/components/common/page-header';
import { FilterBar, type FilterDescriptor } from '@/components/filters/filter-bar';
import { clientsApi, useAssignableUsers, useClientCities, useClientTags } from '@/lib/api/domains';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { useListState } from '@/hooks/use-list-state';

export function ClientListPage() {
  const { t, money } = useI18n();
  const navigate = useNavigate();
  const { can } = useAuth();

  const list = useListState({ defaultSortBy: 'fullName' });
  const { data, isLoading, isFetching, error, refetch } = clientsApi.useList(list.params);
  const { data: cities } = useClientCities();
  const { data: tags } = useClientTags();

  const [formOpen, setFormOpen] = React.useState(false);

  const columns = React.useMemo<DataColumn<ClientDto>[]>(
    () => [
      {
        id: 'fullName',
        header: t('client.fullName'),
        meta: { sortKey: 'fullName', headerLabel: t('client.fullName') },
        cell: ({ row }) => (
          <div className="flex min-w-0 items-center gap-2.5">
            {/* A company client gets its first two letters, a person their initials. */}
            <EntityAvatar
              name={row.original.fullName}
              kind={row.original.clientType === 'individual' ? 'person' : 'company'}
            />
            <div className="min-w-0">
              <p className="truncate font-medium">{row.original.fullName}</p>
              {row.original.contactPerson && (
                <p className="text-muted-soft truncate text-xs">{row.original.contactPerson}</p>
              )}
            </div>
          </div>
        ),
      },
      {
        id: 'clientType',
        header: t('client.type'),
        meta: { sortKey: 'clientType', hideBelow: 'md', headerLabel: t('client.type') },
        cell: ({ row }) => <EnumBadge enum="clientType" value={row.original.clientType} size="sm" />,
      },
      {
        id: 'taxNumber',
        header: t('client.taxNumber'),
        meta: { sortKey: 'taxNumber', numeric: true, hideBelow: 'lg', headerLabel: t('client.taxNumber') },
        cell: ({ row }) => row.original.taxNumber ?? '—',
      },
      {
        id: 'phone',
        header: t('client.phone'),
        meta: { sortKey: 'phone', numeric: true, hideBelow: 'md', headerLabel: t('client.phone') },
        // Click-to-call: for a non-technical user the phone number IS the action.
        cell: ({ row }) =>
          row.original.phone ? (
            <a
              href={`tel:${row.original.phone}`}
              onClick={(event) => event.stopPropagation()}
              className="hover:text-primary tabular hover:underline"
            >
              {row.original.phone}
            </a>
          ) : (
            '—'
          ),
      },
      {
        id: 'city',
        header: t('client.city'),
        meta: { sortKey: 'city', hideBelow: 'xl', headerLabel: t('client.city') },
        cell: ({ row }) => <span className="text-muted-foreground">{row.original.city ?? '—'}</span>,
      },
      {
        id: 'activePolicyCount',
        header: t('client.activePolicies'),
        meta: { sortKey: 'activePolicyCount', align: 'center', numeric: true, headerLabel: t('client.activePolicies') },
        /*
          Vehicles ride inside this column instead of claiming a ninth of their own — the
          list was already at eight, and «what does this client hold» is one question, not
          two. The car glyph is the label, so the header stays as it was.

          The figure is the whole archive; the parenthesis is how much of it is still on
          cover. A client who insured four cars over the years and one today reads «4 (1)»,
          which is the distinction the office asked to keep visible.
        */
        cell: ({ row }) => {
          const vehicles = row.original.vehicleCount ?? 0;
          const activeVehicles = row.original.activeVehicleCount ?? 0;
          return (
            <span className="inline-flex items-center gap-1.5">
              <span className="font-medium">{row.original.activePolicyCount ?? 0}</span>
              {(row.original.openClaimCount ?? 0) > 0 && (
                <Badge tone="serious" size="sm">
                  {row.original.openClaimCount}
                </Badge>
              )}
              {vehicles > 0 && (
                <span
                  className="text-muted-soft inline-flex items-center gap-1 text-xs"
                  title={t('client.vehicles')}
                >
                  <Car className="size-3.5" />
                  <span className="tabular">
                    {vehicles}
                    {activeVehicles !== vehicles && ` (${activeVehicles})`}
                  </span>
                </span>
              )}
            </span>
          );
        },
      },
      {
        id: 'totalPremium',
        header: t('policy.premium'),
        meta: { sortKey: 'totalPremium', align: 'right', numeric: true, hideBelow: 'lg', headerLabel: t('policy.premium') },
        cell: ({ row }) => money(row.original.totalPremium),
      },
      {
        id: 'tags',
        header: t('client.tags'),
        meta: { sortKey: 'tags', hideBelow: 'xl', headerLabel: t('client.tags') },
        cell: ({ row }) => (
          <div className="flex flex-wrap gap-1">
            {row.original.tags.slice(0, 2).map((tag) => (
              <Badge key={tag} tone="outline" size="sm">
                {tag}
              </Badge>
            ))}
            {row.original.tags.length > 2 && (
              <Badge tone="outline" size="sm">
                +{row.original.tags.length - 2}
              </Badge>
            )}
          </div>
        ),
      },
      {
        id: 'actions',
        header: '',
        meta: { width: '3rem', align: 'right' },
        cell: ({ row }) => (
          <div className="row-actions" onClick={(event) => event.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon-sm" aria-label={t('action.more')}>
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => navigate(`/clients/${row.original.id}`)}>
                  {t('action.view')}
                </DropdownMenuItem>
                {can('policy:write') && (
                  <DropdownMenuItem onSelect={() => navigate(`/clients/${row.original.id}?new=policy`)}>
                    <FilePlus2 /> {t('action.newPolicy')}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ),
      },
    ],
    [t, money, navigate, can],
  );

  /*
   * One declaration, two homes: a filter naming a `column` is rendered in the table's
   * filter row under that column, the rest stay in the toolbar, and the narrow-screen
   * popover keeps all of them.
   */
  const filters: FilterDescriptor[] = [
    { key: 'clientType', type: 'enum', enum: 'clientType', label: t('client.type'), column: 'clientType' },
    {
      key: 'city',
      column: 'city',
      type: 'options',
      label: t('client.city'),
      options: (cities ?? []).map((city) => ({ value: city, label: city })),
    },
    {
      key: 'tags',
      column: 'tags',
      type: 'options',
      label: t('client.tags'),
      multiple: true,
      options: (tags ?? []).map((tag) => ({ value: tag, label: tag })),
    },
    /*
      «Ποιος έχει αυτό το αυτοκίνητο;» asked from the client book itself. The same lookup
      exists on the policies list, but the answer wanted here is the owner, not the
      contract — and the search box above now takes a plate too, for whoever types it there.
    */
    { key: 'plateNumber', type: 'text', label: t('policy.plate'), placeholder: t('policy.plate') },
  ];

  return (
    <>
      <PageHeader
        title={t('client.title')}
        description={t('client.subtitle')}
        actions={
          can('client:write') && (
            <Button onClick={() => setFormOpen(true)}>
              <Plus /> {t('action.newClient')}
            </Button>
          )
        }
      />

      <FilterBar
        className="mb-5"
        listState={list}
        inline="unbound"
        searchPlaceholder={`${t('client.fullName')} / ${t('client.taxNumber')} / ${t('client.phone')}`}
        filters={filters}
      />

      <DataTable
        columns={columns}
        rows={data?.data}
        total={data?.meta.total}
        listState={list}
        filters={filters}
        isLoading={isLoading}
        isFetching={isFetching}
        error={error}
        onRetry={refetch}
        getRowId={(client) => client.id}
        onRowClick={(client) => navigate(`/clients/${client.id}`)}
      />

      <ClientFormDialog open={formOpen} onOpenChange={setFormOpen} />
    </>
  );
}

export function ClientFormDialog({
  open,
  onOpenChange,
  client,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client?: ClientDto;
}) {
  const { t } = useI18n();
  const { data: agents } = useAssignableUsers();

  const create = clientsApi.useCreate<CreateClientInput>();
  const update = clientsApi.useUpdate<Partial<CreateClientInput>>();
  const isEdit = Boolean(client);
  const formId = 'client-form';

  const fields: FieldDescriptor<Record<string, unknown>>[] = [
    { name: 'clientType', type: 'enum', enum: 'clientType', label: t('client.type'), required: true },
    { name: 'fullName', type: 'text', label: t('client.fullName'), required: true },
    {
      name: 'taxNumber',
      type: 'text',
      label: t('client.taxNumber'),
      // The checksum lives in the shared schema, so this hint and the rule agree.
      hint: 'ΑΦΜ — 9 ψηφία',
    },
    {
      name: 'contactPerson',
      type: 'text',
      label: t('client.contactPerson'),
      // Companies only — matches the refine() in the shared schema.
      visible: (values) => values.clientType === 'company',
    },
    { name: 'idNumber', type: 'text', label: t('client.idNumber'), visible: (v) => v.clientType === 'individual' },
    { name: 'dateOfBirth', type: 'date', label: t('client.dateOfBirth'), visible: (v) => v.clientType === 'individual' },
    { name: 'phone', type: 'tel', label: t('client.phone') },
    { name: 'email', type: 'email', label: t('client.email') },
    { name: 'address', type: 'text', label: t('client.address') },
    { name: 'city', type: 'text', label: t('client.city') },
    { name: 'postalCode', type: 'text', label: t('client.postalCode') },
    { name: 'iban', type: 'text', label: t('client.iban') },
    {
      name: 'assignedAgentId',
      type: 'select',
      label: t('client.agent'),
      options: (agents ?? []).map((agent) => ({ value: agent.id, label: agent.fullName })),
    },
    { name: 'marketingConsent', type: 'switch', label: t('client.marketingConsent') },
    { name: 'notes', type: 'textarea', label: t('common.notes') },
  ];

  const submit = async (values: Record<string, unknown>) => {
    const payload = { ...values };
    // Empty optional strings must not reach a schema expecting a valid AFM/IBAN/email.
    for (const key of ['taxNumber', 'email', 'iban', 'dateOfBirth'] as const) {
      if (!payload[key]) delete payload[key];
    }

    /**
     * An unselected agent is sent as an explicit `null`, not omitted.
     *
     * Omitting it left the server unable to tell "the user chose nobody" from "this
     * caller does not manage assignment", so it fell back to assigning the creator — and
     * a client saved with the field deliberately blank came back owned by whoever typed
     * it in. `null` states the choice; an absent field still defaults, which is what the
     * import path wants.
     */
    payload.assignedAgentId = payload.assignedAgentId || null;

    if (isEdit && client) {
      await update.mutateAsync({ id: client.id, input: payload as Partial<CreateClientInput> });
    } else {
      await create.mutateAsync(payload as CreateClientInput);
    }
    toast.success(t('common.saved'));
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? t('action.edit') : t('action.newClient')}</DialogTitle>
          <DialogDescription>{t(client ? 'dialog.editClientHint' : 'dialog.newClientHint')}</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <AutoForm
            id={formId}
            schema={isEdit ? createClientSchema.innerType().partial() : createClientSchema}
            defaultValues={
              {
                clientType: client?.clientType ?? 'individual',
                fullName: client?.fullName ?? '',
                taxNumber: client?.taxNumber ?? '',
                idNumber: client?.idNumber ?? '',
                dateOfBirth: client?.dateOfBirth ?? '',
                contactPerson: client?.contactPerson ?? '',
                phone: client?.phone ?? '',
                email: client?.email ?? '',
                address: client?.address ?? '',
                city: client?.city ?? '',
                postalCode: client?.postalCode ?? '',
                iban: client?.iban ?? '',
                assignedAgentId: client?.assignedAgentId ?? '',
                marketingConsent: client?.marketingConsent ?? false,
                notes: client?.notes ?? '',
                tags: client?.tags ?? [],
              } as never
            }
            fields={fields}
            onSubmit={submit}
          />
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('action.cancel')}
          </Button>
          <Button type="submit" form={formId} loading={create.isPending || update.isPending}>
            {t('action.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
